import { computed, reactive, readonly } from 'vue';
import { storefrontBlob, storefrontRequest } from '@/lib/storefront-api';

// One shared thread for the whole storefront: the bubble and the full page show the
// same conversation, and a visitor can pick it back up after a reload.
const VISITOR_KEY = 'sh_ai_visitor_id';
const CONVERSATION_KEY = 'sh_ai_chat_conversation';
const LIMIT_KEY = 'sh_ai_limit';

const state = reactive({
  enabled: null, // null = unknown yet
  conversation: '',
  messages: [],
  sending: false,
  loadingHistory: false,
  error: '',
  lastFailed: null, // { text, files } of the message that did not go through
  conversations: [],
  loadingList: false,
  quota: null, // { remaining_messages, remaining_photos, limit_messages, limit_photos, subject }
  // The ChatGPT-style "you've reached your limit" card: { reason, message, resetsAt, locks }
  limit: null,
  now: Date.now(),
  rendering: false,
  // The catalog piece this chat is about (opened from a product page): { id, name, price, image, path, model_glb_url }
  focus: null,
  focusDirty: false, // true until the server has been told about a pin/unpin
});

let clock = null;
let lastQuotaCheck = 0;
const QUOTA_RECHECK_MS = 60000;
function startClock() {
  if (clock || typeof window === 'undefined') return;
  clock = window.setInterval(() => {
    state.now = Date.now();
    if (state.limit?.resetsAt && new Date(state.limit.resetsAt).getTime() <= state.now) clearLimit();
    // While a limit card is shown, ask the server once a minute: the shop may have raised the
    // limit from the back office, or the rolling window may have freed up early.
    if (state.limit && state.now - lastQuotaCheck > QUOTA_RECHECK_MS) refreshQuota();
  }, 1000);
  window.addEventListener('focus', () => { if (state.limit) refreshQuota(); });
}

/** Re-reads today's usage from the server and drops the limit card when there is room again. */
async function refreshQuota() {
  lastQuotaCheck = Date.now();
  try {
    const status = await storefrontRequest('/ai/status');
    if (status.quota) applyQuota(status.quota);
  } catch (e) { /* keep what we have */ }
}

/** Stores the quota and clears a limit card the server no longer agrees with. */
function applyQuota(quota) {
  state.quota = quota || null;
  if (!quota || !state.limit) return;
  const room = (remaining) => remaining === null || remaining === undefined || remaining > 0;
  const freed = {
    messages: room(quota.remaining_messages),
    photos: room(quota.remaining_photos),
    renders: room(quota.remaining_renders),
    blocked: quota.blocked === false,
    daily_budget: quota.budget_exhausted === false,
    monthly_budget: quota.budget_exhausted === false,
    // 'burst' and 'device_daily' come from the throttle, not the quota: only time clears them.
  }[state.limit.reason];
  if (freed) clearLimit();
}

function setLimit(limit) {
  state.limit = limit;
  storage(LIMIT_KEY, limit ? JSON.stringify(limit) : null);
  if (limit) startClock();
}
function clearLimit() {
  state.limit = null;
  state.error = '';
  storage(LIMIT_KEY, null);
}
function restoreLimit() {
  try {
    const saved = JSON.parse(storage(LIMIT_KEY) || 'null');
    if (saved && (!saved.resetsAt || new Date(saved.resetsAt).getTime() > Date.now())) setLimit(saved);
    else storage(LIMIT_KEY, null);
  } catch (e) { storage(LIMIT_KEY, null); }
}

// Reasons that stop the customer from sending anything until the reset (photos only lock the attach button).
const LOCKING = ['messages', 'blocked', 'daily_budget', 'monthly_budget', 'conversation', 'burst', 'device_daily'];

function limitFromError(e) {
  const payload = e.payload || {};
  if (e.status === 429 && !payload.reason) {
    // A throttle that did not say which one it was: trust Retry-After rather than inventing a minute,
    // and only call it a burst when it really is one — a long wait is a daily ceiling, not impatience.
    const retry = Number(e.retryAfter) > 0 ? Number(e.retryAfter) : 60;
    const burst = retry <= 180;
    return {
      reason: burst ? 'burst' : 'device_daily',
      message: burst
        ? 'You are sending requests very quickly — give it a minute and try again.'
        : 'This device has reached the daily safety limit for AI actions.',
      resetsAt: new Date(Date.now() + retry * 1000).toISOString(),
      locks: true,
    };
  }
  if (![403, 429, 503].includes(e.status) || !payload.reason) return null;
  return {
    reason: payload.reason,
    message: payload.message || e.message,
    resetsAt: payload.resets_at || null,
    locks: LOCKING.includes(payload.reason),
    quota: payload.quota || null,
  };
}

let statusPromise = null;

function storage(key, value) {
  try {
    if (value === undefined) return window.localStorage.getItem(key) || '';
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (e) {
    /* private mode: the chat still works for this page view */
  }
  return value || '';
}

// One id per browser so an anonymous visitor can come back to their thread,
// and so the shop can see how many people ask the same things.
export function visitorId() {
  let id = storage(VISITOR_KEY);
  if (!id) {
    const bytes = new Uint8Array(12);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else bytes.forEach((_, i) => (bytes[i] = Math.floor(Math.random() * 256)));
    id = `v_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
    storage(VISITOR_KEY, id);
  }
  return id;
}

async function ensureStatus() {
  if (state.enabled !== null) return state.enabled;
  restoreLimit();
  if (!statusPromise) {
    statusPromise = storefrontRequest('/ai/status')
      .then((status) => {
        state.enabled = Boolean(status.enabled && (status.features?.chat ?? true));
        applyQuota(status.quota || null);
      })
      .catch(() => { state.enabled = false; });
  }
  await statusPromise;
  return state.enabled;
}

function rememberConversation(uuid) {
  state.conversation = uuid || '';
  storage(CONVERSATION_KEY, uuid || null);
}

async function loadHistory(uuid = state.conversation || storage(CONVERSATION_KEY)) {
  if (!uuid) {
    state.messages = [];
    return;
  }
  state.loadingHistory = true;
  state.error = '';
  try {
    const response = await storefrontRequest(`/ai/chat/${uuid}`, { params: { visitor_id: visitorId() } });
    state.messages = response.data?.messages || [];
    state.focus = response.data?.focus || null;
    state.focusDirty = false;
    rememberConversation(uuid);
  } catch (e) {
    // Not ours any more (cleared server-side, or a different account): start fresh.
    rememberConversation('');
    state.messages = [];
  } finally {
    state.loadingHistory = false;
  }
}

async function listConversations() {
  state.loadingList = true;
  try {
    const response = await storefrontRequest('/ai/chat', { params: { visitor_id: visitorId() } });
    state.conversations = response.data || [];
  } catch (e) {
    state.conversations = [];
  } finally {
    state.loadingList = false;
  }
}

function startNew({ focus = null } = {}) {
  state.messages = [];
  state.error = '';
  state.lastFailed = null;
  state.focus = focus;
  state.focusDirty = Boolean(focus);
  rememberConversation('');
  // A long-conversation cap is the only limit a new thread clears.
  if (state.limit?.reason === 'conversation') clearLimit();
}

async function openConversation(uuid) {
  if (uuid === state.conversation && state.messages.length) return;
  state.messages = [];
  state.focus = null;
  await loadHistory(uuid);
}

/** Pin the piece the chat is about (null unpins). The server learns it with the next message. */
function setFocus(product) {
  if ((product?.id || null) === (state.focus?.id || null)) return;
  state.focus = product || null;
  state.focusDirty = true;
}

function localAttachments(files) {
  return files.map((file) => ({ name: file.name, url: URL.createObjectURL(file), local: true }));
}

async function send({ text = '', files = [], page = '' } = {}) {
  const content = text.trim();
  const photos = files.filter(Boolean).slice(0, 3);
  if ((!content && !photos.length) || state.sending) return false;
  if (state.limit?.locks) return false;

  state.error = '';
  state.lastFailed = null;

  const pending = {
    key: `local-${Date.now()}`,
    role: 'user',
    content: content || '(photo)',
    attachments: localAttachments(photos),
    created_at: new Date().toISOString(),
  };
  state.messages.push(pending);
  state.sending = true;

  try {
    let response;
    // The pinned piece travels with the first message (and whenever it changes); 0 unpins it.
    const focusId = state.focusDirty || !state.conversation ? (state.focus?.id || 0) : null;
    if (photos.length) {
      const form = new FormData();
      form.append('message', content);
      photos.forEach((file) => form.append('images[]', file));
      if (state.conversation) form.append('conversation', state.conversation);
      form.append('visitor_id', visitorId());
      form.append('page', page);
      if (focusId !== null) form.append('product_id', String(focusId));
      response = await storefrontRequest('/ai/chat', { method: 'POST', data: form });
    } else {
      response = await storefrontRequest('/ai/chat', {
        method: 'POST',
        data: { message: content, conversation: state.conversation || null, visitor_id: visitorId(), page, ...(focusId !== null ? { product_id: focusId } : {}) },
      });
    }
    const data = response.data || {};
    if ('focus' in data) {
      state.focus = data.focus || null;
      state.focusDirty = false;
    }
    if (data.quota) {
      applyQuota(data.quota);
      // Last allowed message: show the card right away, like ChatGPT does.
      if (data.quota.remaining_messages === 0 && data.quota.limit_messages > 0) {
        setLimit({ reason: 'messages', message: `You have reached your limit of ${data.quota.limit_messages} messages per 24 hours.`, resetsAt: data.quota.resets_at, locks: true, quota: data.quota });
      }
    }
    rememberConversation(data.conversation || state.conversation);
    const index = state.messages.indexOf(pending);
    const saved = data.user_message || pending;
    // Keep the local previews until the stored copies are reachable.
    if (saved.attachments?.some((a) => !a.url)) saved.attachments = pending.attachments;
    state.messages.splice(index, 1, saved);
    if (data.message) state.messages.push(data.message);
    return true;
  } catch (e) {
    state.messages.splice(state.messages.indexOf(pending), 1);
    state.lastFailed = { text: content, files: photos };
    const limit = limitFromError(e);
    if (limit) {
      // A quota, a block or the budget: shown as a card in the thread, not as an error line.
      if (e.payload?.quota) applyQuota(e.payload.quota);
      setLimit(limit);
      state.lastFailed = limit.locks ? null : { text: content, files: [] };
      return false;
    }
    state.error = (e.status === 422 && e.errors ? Object.values(e.errors).flat()[0] : null) || e.message || 'Something went wrong. Please try again.';
    return false;
  } finally {
    state.sending = false;
  }
}

// Any user photo in the thread can be used for "show it in my room".
function latestRoomPhoto() {
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    const m = state.messages[i];
    if (m.role === 'user' && m.attachments?.some((a) => a.url && !a.local)) return m;
  }
  return null;
}

/**
 * "Show it in my room": product + (optionally) a composite where the customer placed the 3D model.
 * A placeholder bubble shows while the picture is generated (20–40 s).
 */
async function render({ product, composite = null, placement = '' } = {}) {
  if (!product || state.sending || state.rendering || !state.conversation) return false;
  state.error = '';
  state.rendering = true;

  const placeholder = { key: `render-${Date.now()}`, role: 'assistant', content: '', rendering: true, product, created_at: new Date().toISOString() };
  state.messages.push(placeholder);

  try {
    const form = new FormData();
    form.append('product_id', product.id);
    form.append('visitor_id', visitorId());
    if (placement) form.append('placement', placement);
    if (composite) form.append('composite', composite, 'composite.jpg');
    const photo = latestRoomPhoto();
    if (photo?.id && !composite) form.append('message_id', photo.id);
    const response = await storefrontRequest(`/ai/chat/${state.conversation}/render`, { method: 'POST', data: form });
    const data = response.data || {};
    if (data.quota) applyQuota(data.quota);
    const index = state.messages.indexOf(placeholder);
    state.messages.splice(index, 1, ...[data.user_message, data.message].filter(Boolean));
    return true;
  } catch (e) {
    const recorded = e.payload?.data;
    if (recorded?.message) {
      // The assistant answered instead of drawing (e.g. no sensible spot in this photo) — show its reply.
      if (recorded.quota) applyQuota(recorded.quota);
      state.messages.splice(state.messages.indexOf(placeholder), 1, ...[recorded.user_message, recorded.message].filter(Boolean));
      return false;
    }
    state.messages.splice(state.messages.indexOf(placeholder), 1);
    const limit = limitFromError(e);
    if (limit) {
      if (e.payload?.quota) applyQuota(e.payload.quota);
      setLimit(limit);
      return false;
    }
    state.error = e.message || 'The picture could not be created. Please try again.';
    return false;
  } finally {
    state.rendering = false;
  }
}

/**
 * What "draw it" refers to: the request the assistant drafted most recently, otherwise the
 * customer's last real description (not a "Design:" / "Show me …" line). '' when nothing fits.
 */
function lastDescription() {
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    const m = state.messages[i];
    if (m.role === 'assistant' && m.payload?.quote_draft) {
      return [m.payload.quote_draft.title, m.payload.quote_draft.description].filter(Boolean).join('. ');
    }
    if (m.role === 'user' && m.content && !/^(Design:|Change the design:|Show me the |Send my quotation request|Request a quotation)/.test(m.content) && m.content !== '(photo)' && m.content.split(/\s+/).length >= 3) {
      return m.content;
    }
  }
  return '';
}

/** The newest design picture in the thread, if any (Draw mode applies changes to it). */
function latestDesign() {
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    const m = state.messages[i];
    if (m.role === 'assistant' && m.payload?.design && m.attachments?.[0]?.url) return m;
  }
  return null;
}

/** "Draw it": a picture from words; with `basedOn` (a design message) the change is applied to that picture. */
async function design({ text, basedOn = null, photoMessage = null, files = [], imagesRole = 'base' } = {}) {
  if (!text || state.sending || state.rendering) return false;
  state.error = '';
  state.rendering = true;
  const placeholder = { key: `design-${Date.now()}`, role: 'assistant', content: '', rendering: true, label: basedOn ? 'Updating your design…' : 'Drawing your design…', created_at: new Date().toISOString() };
  const drawIn = !basedOn && (photoMessage || (files.length && imagesRole === 'base'));
  const userLine = { key: `design-user-${Date.now()}`, role: 'user', content: (basedOn ? 'Change the design: ' : (drawIn ? 'Change my photo: ' : 'Design: ')) + text, attachments: files.map((f) => ({ name: f.name, url: URL.createObjectURL(f), local: true })), created_at: new Date().toISOString() };
  state.messages.push(userLine, placeholder);
  try {
    const fields = { prompt: text, based_on: basedOn?.id || null, photo_message_id: !basedOn && photoMessage?.id ? photoMessage.id : null, images_role: imagesRole, conversation: state.conversation || null, visitor_id: visitorId() };
    let body = fields;
    if (files.length) {
      body = new FormData();
      Object.entries(fields).forEach(([key, value]) => { if (value !== null && value !== undefined) body.append(key, value); });
      files.forEach((file) => body.append('images[]', file, file.name));
    }
    const response = await storefrontRequest('/ai/chat/design', { method: 'POST', data: body });
    const data = response.data || {};
    if (data.conversation && data.conversation !== state.conversation) rememberConversation(data.conversation);
    if (data.quota) applyQuota(data.quota);
    state.messages.splice(state.messages.indexOf(userLine), 2, ...[data.user_message, data.message].filter(Boolean));
    return true;
  } catch (e) {
    const recorded = e.payload?.data;
    if (recorded?.message) {
      state.messages.splice(state.messages.indexOf(userLine), 2, ...[recorded.user_message, recorded.message].filter(Boolean));
      return false;
    }
    state.messages.splice(state.messages.indexOf(userLine), 2);
    const limit = limitFromError(e);
    if (limit) {
      if (e.payload?.quota) applyQuota(e.payload.quota);
      setLimit(limit);
      return false;
    }
    state.error = e.message || 'The picture could not be created. Please try again.';
    return false;
  } finally {
    state.rendering = false;
  }
}

/** Builds one quotation request from the whole brief of a design message; the card then lets the customer send it. */
/** The same design from another camera angle — costs one picture. */
async function designView(message, angle) {
  if (!message?.id || !state.conversation || state.sending || state.rendering) return false;
  state.error = '';
  state.rendering = true;
  const placeholder = { key: `view-${Date.now()}`, role: 'assistant', content: '', rendering: true, created_at: new Date().toISOString() };
  state.messages.push(placeholder);
  try {
    const response = await storefrontRequest(`/ai/chat/${state.conversation}/design/${message.id}/view`, {
      method: 'POST',
      data: { angle, visitor_id: visitorId() },
    });
    const data = response.data || {};
    if (data.quota) applyQuota(data.quota);
    state.messages.splice(state.messages.indexOf(placeholder), 1, ...[data.user_message, data.message].filter(Boolean));
    return true;
  } catch (e) {
    state.messages.splice(state.messages.indexOf(placeholder), 1);
    const limit = limitFromError(e);
    if (limit) {
      if (e.payload?.quota) applyQuota(e.payload.quota);
      setLimit(limit);
      return false;
    }
    state.error = e.message || 'That view could not be created. Please try again.';
    return false;
  } finally {
    state.rendering = false;
  }
}

/**
 * Save a picture to the customer's device. A plain <a download> cannot work here: the file lives on
 * the API host, and a signed-in customer's link would not carry their token — so fetch it with the
 * usual headers and save the blob under the name the server chose (Content-Disposition).
 */
async function downloadPicture(message, index = 0) {
  const attachment = message?.attachments?.[index];
  if (!attachment?.url || !message?.id || !state.conversation) return false;
  const fallbackName = pictureName(message);
  try {
    const blob = await storefrontBlob(`/ai/chat/${state.conversation}/photo/${message.id}/${index}`, {
      download: 1,
      visitor_id: visitorId(),
    });
    saveBlob(blob.data, blob.filename || fallbackName);
    return true;
  } catch (e) {
    // Last resort: let the browser fetch it directly (the server still names the file).
    window.open(attachment.url, '_blank', 'noopener');
    return false;
  }
}

/** The same name the server builds, used when the header is not readable. */
function pictureName(message) {
  const design = message?.payload?.design;
  const render = message?.payload?.render;
  const slug = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const date = (message?.created_at ? new Date(message.created_at) : new Date()).toISOString().slice(0, 10);
  const bits = ['sweet-home-gallery'];
  if (design) {
    bits.push(design.source === 'photo' ? 'my-room' : 'design', `v${design.version || 1}`);
    if (design.view_label) bits.push(slug(design.view_label));
  } else if (render) {
    bits.push(slug(render.product?.name) || 'piece', 'in-my-room');
  } else {
    bits.push('picture');
  }
  bits.push(date);
  return `${bits.filter(Boolean).join('-')}.jpg`;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

async function designQuote(message) {
  if (!message?.id || !state.conversation || state.sending) return false;
  state.error = '';
  state.sending = true;
  const pending = { key: `dq-${Date.now()}`, role: 'user', content: 'Request a quotation for this design', created_at: new Date().toISOString() };
  state.messages.push(pending);
  try {
    const response = await storefrontRequest(`/ai/chat/${state.conversation}/design/${message.id}/quote`, { method: 'POST', data: { visitor_id: visitorId() } });
    const data = response.data || {};
    if (data.quota) applyQuota(data.quota);
    state.messages.splice(state.messages.indexOf(pending), 1, ...[data.user_message, data.message].filter(Boolean));
    return true;
  } catch (e) {
    const recorded = e.payload?.data;
    if (recorded?.message) {
      state.messages.splice(state.messages.indexOf(pending), 1, ...[recorded.user_message, recorded.message].filter(Boolean));
      return false;
    }
    state.messages.splice(state.messages.indexOf(pending), 1);
    state.error = e.message || 'The request could not be prepared. Please try again.';
    return false;
  } finally {
    state.sending = false;
  }
}

/** Sends the quotation request the assistant drafted in `message` (edited fields in `payload`). */
async function sendQuote(message, payload) {
  if (!state.conversation || !message?.id) throw new Error('This draft is no longer available.');
  const { files = [], ...fields } = payload;
  let body = { ...fields, message_id: message.id, visitor_id: visitorId() };
  if (files.length) {
    const form = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) value.forEach((v) => form.append(`${key}[]`, v));
      else form.append(key, value);
    });
    files.forEach((file) => form.append('reference_images[]', file, file.name));
    body = form;
  }
  const response = await storefrontRequest(`/ai/chat/${state.conversation}/quote`, { method: 'POST', data: body });
  const data = response.data || {};
  const index = state.messages.findIndex((m) => m.id === message.id);
  if (index >= 0 && data.draft_message) state.messages.splice(index, 1, data.draft_message);
  state.messages.push(...[data.user_message, data.message].filter(Boolean));
  return data.request;
}

async function retry(page = '') {
  const failed = state.lastFailed;
  if (!failed) return false;
  return send({ ...failed, page });
}

export function useAiChat() {
  return {
    state: readonly(state),
    enabled: computed(() => state.enabled === true),
    lastFollowUps: computed(() => {
      const last = state.messages[state.messages.length - 1];
      return last && last.role === 'assistant' && last.status !== 'error' ? last.payload?.follow_ups || [] : [];
    }),
    ensureStatus,
    loadHistory,
    listConversations,
    openConversation,
    startNew,
    setFocus,
    focus: computed(() => state.focus),
    send,
    render,
    design,
    designQuote,
    designView,
    downloadPicture,
    sendQuote,
    setError: (message) => { state.error = message || ''; },
    retry,
    visitorId,
    hasRoomPhoto: computed(() => Boolean(latestRoomPhoto())),
    latestDesign: computed(() => latestDesign()),
    latestRoomPhoto,
    lastDescription,
    rendersLeft: computed(() => (state.quota ? state.quota.remaining_renders : null)),
    storedConversation: () => storage(CONVERSATION_KEY),
    limit: computed(() => state.limit),
    // "2 h 13 m" / "45 s" until the limit frees up; '' when unknown
    limitCountdown: computed(() => {
      if (!state.limit?.resetsAt) return '';
      const ms = new Date(state.limit.resetsAt).getTime() - state.now;
      if (ms <= 0) return 'now';
      const total = Math.ceil(ms / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const sec = total % 60;
      if (h > 0) return `${h} h ${String(m).padStart(2, '0')} min`;
      if (m > 0) return `${m} min ${String(sec).padStart(2, '0')} s`;
      return `${sec} s`;
    }),
    locked: computed(() => Boolean(state.limit?.locks)),
    photosLocked: computed(() => state.limit?.reason === 'photos' || (state.quota?.remaining_photos === 0 && state.quota?.limit_photos > 0)),
    quotaHint: computed(() => {
      const q = state.quota;
      if (!q) return '';
      const bits = [];
      if (q.remaining_messages !== null && q.remaining_messages !== undefined && q.remaining_messages <= 5) bits.push(`${q.remaining_messages} message${q.remaining_messages === 1 ? '' : 's'} left today`);
      if (q.remaining_photos !== null && q.remaining_photos !== undefined && q.remaining_photos <= 2) bits.push(`${q.remaining_photos} photo${q.remaining_photos === 1 ? '' : 's'} left today`);
      if (!bits.length) return '';
      return bits.join(' · ') + (q.subject !== 'account' ? ' — sign in for a higher limit.' : '.');
    }),
  };
}
